import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { Member, UserRole } from '../types';
import { useClickSound } from '../hooks/useClickSound';

interface MemberCardProps {
  member: Member;
  canVerify?: boolean;
  onVerify?: (memberId: string) => Promise<void>;
  onUnverify?: (memberId: string) => Promise<void>;
  verifyLoading?: boolean;
  onClick?: (member: Member) => void;
}

const gradients = [
  'linear-gradient(135deg, #14F195, #00C2FF)',
  'linear-gradient(135deg, #9945FF, #14F195)',
  'linear-gradient(135deg, #00C2FF, #9945FF)',
  'linear-gradient(135deg, #ec4899, #8b5cf6)',
  'linear-gradient(135deg, #3b82f6, #06b6d4)',
  'linear-gradient(135deg, #10b981, #14b8a6)',
  'linear-gradient(135deg, #f97316, #ef4444)',
  'linear-gradient(135deg, #8b5cf6, #d946ef)',
];

function getAvatarGradient(username: string): string {
  const index = username.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % gradients.length;
  return gradients[index];
}

// Role badge colors
const roleBadgeStyles: Record<UserRole, { bg: string; color: string; label: string }> = {
  lead: { bg: 'rgba(20, 241, 149, 0.15)', color: '#14F195', label: 'Lead' },
  lieutenant: { bg: 'rgba(153, 69, 255, 0.15)', color: '#9945FF', label: 'Lieutenant' },
  member: { bg: 'transparent', color: 'transparent', label: '' },
};

export default function MemberCard({ 
  member, 
  canVerify = false,
  onVerify,
  onUnverify,
  verifyLoading = false,
  onClick,
}: MemberCardProps) {
  const { playClickSound } = useClickSound();
  const [isHovered, setIsHovered] = useState(false);
  const [tickHovered, setTickHovered] = useState(false);
  const gradient = getAvatarGradient(member.xUsername);
  
  const handleCardClick = () => {
    if (member.isPending) return; // Don't open profile for pending members
    if (onClick) {
      playClickSound();
      onClick(member);
    }
  };
  
  const handleVerifyClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canVerify || verifyLoading) return;
    
    playClickSound();
    if (member.isVerified && onUnverify) {
      await onUnverify(member.id);
    } else if (!member.isVerified && onVerify) {
      await onVerify(member.id);
    }
  };
  
  const cardStyle: CSSProperties = {
    padding: '12px',
    borderRadius: '12px',
    background: member.isPending 
      ? 'rgba(255,255,255,0.01)' 
      : (isHovered ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)'),
    border: member.isPending
      ? '1px solid rgba(255,255,255,0.03)'
      : `1px solid ${isHovered ? 'rgba(20,241,149,0.2)' : 'rgba(255,255,255,0.05)'}`,
    transition: 'all 0.2s',
    cursor: member.isPending ? 'default' : 'pointer',
    opacity: member.isPending ? 0.7 : 1,
  };

  const avatarStyle: CSSProperties = {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    objectFit: 'cover',
    border: '1px solid rgba(255,255,255,0.1)',
  };

  const avatarFallbackStyle: CSSProperties = {
    ...avatarStyle,
    background: gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 700,
    fontSize: '14px',
  };

  const skillStyle: CSSProperties = {
    padding: '2px 8px',
    fontSize: '10px',
    borderRadius: '4px',
    background: 'rgba(255,255,255,0.05)',
    color: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(255,255,255,0.05)',
  };

  const tickColor = member.isVerified ? '#14F195' : 'rgba(255,255,255,0.3)';
  const tickHoverColor = canVerify 
    ? (member.isVerified ? '#ef4444' : '#14F195') 
    : tickColor;

  const roleBadge = roleBadgeStyles[member.role || 'member'];

  return (
    <div 
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Avatar */}
        <div style={{ position: 'relative' }}>
          {member.xAvatarUrl ? (
            <img src={member.xAvatarUrl} alt={member.xDisplayName} style={avatarStyle} />
          ) : (
            <div style={avatarFallbackStyle}>
              {(member.name || member.xDisplayName).charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <h4 style={{ fontWeight: 500, color: member.isPending ? 'rgba(255,255,255,0.6)' : 'white', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {member.name || member.xDisplayName}
            </h4>
            
            {/* Pending Badge */}
            {member.isPending && (
              <span style={{
                padding: '2px 6px',
                fontSize: '9px',
                fontWeight: 600,
                borderRadius: '4px',
                background: 'rgba(255, 193, 7, 0.15)',
                color: '#FFC107',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Pending
              </span>
            )}
            
            {/* Role Badge */}
            {member.role && member.role !== 'member' && !member.isPending && (
              <span style={{
                padding: '2px 6px',
                fontSize: '9px',
                fontWeight: 600,
                borderRadius: '4px',
                background: roleBadge.bg,
                color: roleBadge.color,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                {roleBadge.label}
              </span>
            )}
          </div>
          {member.isPending ? (
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>
              @{member.xUsername}
            </span>
          ) : (
            <a
              href={`https://x.com/${member.xUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', textDecoration: 'none' }}
              onClick={(e) => e.stopPropagation()}
            >
              @{member.xUsername}
            </a>
          )}
          
          {member.bio && (
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '6px', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {member.bio}
            </p>
          )}
          
          {member.skills && member.skills.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
              {member.skills.slice(0, 3).map((skill) => (
                <span key={skill} style={skillStyle}>{skill}</span>
              ))}
              {member.skills.length > 3 && (
                <span style={{ ...skillStyle, background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.3)' }}>
                  +{member.skills.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Verification Tick */}
        {!member.isPending && (
          <button
            onClick={handleVerifyClick}
            onMouseEnter={() => setTickHovered(true)}
            onMouseLeave={() => setTickHovered(false)}
            disabled={verifyLoading}
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '8px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: tickHovered && canVerify ? tickHoverColor : tickColor,
              cursor: canVerify ? 'pointer' : 'default',
              transition: 'all 0.2s',
              flexShrink: 0,
              opacity: verifyLoading ? 0.5 : 1,
            }}
            title={
              canVerify 
                ? (member.isVerified ? 'Click to unverify' : 'Click to verify')
                : (member.isVerified ? 'Verified member' : 'Not verified')
            }
          >
            {verifyLoading ? (
              <div style={{ 
                width: '14px', 
                height: '14px', 
                border: '2px solid rgba(255,255,255,0.2)', 
                borderTopColor: '#14F195', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite' 
              }} />
            ) : (
              <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
              </svg>
            )}
          </button>
        )}
        {member.isPending && (
          <div
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '8px',
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFC107',
              flexShrink: 0,
            }}
            title="Pre-verified - will be verified when they sign up"
          >
            <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}
